// src/configs/firebaseAuth.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore/lite';
import { getStorage } from 'firebase/storage';
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  deleteUser,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

import firebaseConfig from 'configs/FirebaseConfig';

// ─────────────────────────────────────────────────────────────────────────────
// 1) Initialize (avoid double init under HMR)
// ─────────────────────────────────────────────────────────────────────────────

const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

// ─────────────────────────────────────────────────────────────────────────────
// 2) Services
// ─────────────────────────────────────────────────────────────────────────────

const auth = getAuth(firebaseApp);

const db = getFirestore(firebaseApp);

const storage = getStorage(firebaseApp);

// ─────────────────────────────────────────────────────────────────────────────
// 3) Providers
// ─────────────────────────────────────────────────────────────────────────────

const googleAuthProvider = new GoogleAuthProvider();

const facebookAuthProvider = new FacebookAuthProvider();

// ─────────────────────────────────────────────────────────────────────────────
// 4) Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function setAuthPersistence(mode = 'local') {
  const persistence = mode === 'session' ? browserSessionPersistence : browserLocalPersistence;
  await setPersistence(auth, persistence);
}

function getCurrentUserOnce() {
  return new Promise(resolve => {
    const unsub = onAuthStateChanged(auth, user => {
      unsub();
      resolve(user ?? null);
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 5) Exports
// ─────────────────────────────────────────────────────────────────────────────

export {
  firebaseApp,
  auth,
  db,
  storage,
  googleAuthProvider,
  facebookAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  deleteUser,
  onAuthStateChanged,
  setAuthPersistence,
  getCurrentUserOnce,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  reauthenticateWithCredential,
  updatePassword,
};
